/**
 * @file UnpublishLegalDialog.tsx
 * @description Окно «Снять с публикации» (phase19.16, SB16, SB125, SB143;
 * uiux-legal-steps-b-c §9.1): что изменится на сайте по `usage` документа,
 * что будет с редакциями и как вернуть («Показать снова» — та же редакция).
 *
 * Оферта и политика ПДн — красная полоса и опасная кнопка «Снять всё равно»
 * (вопрос 11 §12: без галочки). Ошибка сервера — в окне с «Повторить»:
 * документ остаётся на сайте, человек должен это прочитать. На телефоне —
 * во весь экран.
 */

import { useState } from "react";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { NoticeStrip } from "../ui/NoticeStrip";
import { unpublishLegalDoc, type LegalDocumentDto, type LegalUnpublishResultDto } from "../../lib/api-legal";
import { legalErrorCode, legalErrorText } from "../../lib/legal/legal-errors";
import { isRequiredLegalDoc, requiredDocUnpublishWarning, unpublishConsequences, unpublishRevisionNote } from "../../lib/legal/legal-usage-lines";

interface Props {
  /** Документ; null — окно закрыто. */
  doc: LegalDocumentDto | null;
  onClose: () => void;
  onDone: (result: LegalUnpublishResultDto) => void;
}

/** Текст сбоя: документ по-прежнему на сайте. */
const FAILED_TEXT = "Не удалось снять документ. Он по-прежнему на сайте.";

export function UnpublishLegalDialog({ doc, onClose, onDone }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const required = doc ? isRequiredLegalDoc(doc) : false;
  const note = doc ? unpublishRevisionNote(doc) : null;

  const close = () => {
    if (busy) return;
    setError(null);
    onClose();
  };

  async function submit() {
    if (!doc || busy) return;
    setBusy(true);
    setError(null);
    try {
      onDone(await unpublishLegalDoc(doc.id));
    } catch (err) {
      // Код с понятным текстом — его и показываем; иначе общий «по-прежнему на сайте».
      setError(legalErrorCode(err) ? legalErrorText(err, FAILED_TEXT) : FAILED_TEXT);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AlertDialog open={doc !== null} onOpenChange={(open) => (!open ? close() : null)}>
      <AlertDialogContent fullScreen className="max-w-md gap-3 sm:max-w-[30rem]">
        <AlertDialogHeader>
          <AlertDialogTitle>Снять с публикации «{doc?.title}»?</AlertDialogTitle>
          <AlertDialogDescription>Документ пропадёт с сайта сразу — публиковать оформление не нужно.</AlertDialogDescription>
        </AlertDialogHeader>
        {doc && required ? <NoticeStrip tone="error">{requiredDocUnpublishWarning(doc)}</NoticeStrip> : null}
        {doc ? (
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-on-surface">Что изменится:</span>
            <ul className="type-body flex list-disc flex-col gap-1 pl-5 text-on-surface">
              {unpublishConsequences(doc).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {note ? <p className="type-body text-on-surface-variant">{note}</p> : null}
        {error ? (
          <NoticeStrip
            tone="error"
            action={
              <Button variant="outline" size="sm" onClick={() => void submit()} disabled={busy}>
                Повторить
              </Button>
            }
          >
            {error}
          </NoticeStrip>
        ) : null}
        <AlertDialogFooter>
          <Button variant="ghost" className="max-sm:flex-1" onClick={close} disabled={busy}>
            Отмена
          </Button>
          <Button variant={required ? "destructive-solid" : "default"} className="max-sm:flex-1" loading={busy} onClick={() => void submit()}>
            {required ? "Снять всё равно" : "Снять с публикации"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
